import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Briefcase, CheckCircle2, MapPin, Clock3, Users, DownloadCloud, Plus, ChevronDown, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { todayLocal, timeZone } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { JobRow, PendingRow, OnSiteRow, WorkerRow } from "@/components/coord/OverviewLists";

const CARDS = [
  { key: "total_jobs", label: "Total jobs", icon: Briefcase, color: "text-primary" },
  { key: "active", label: "Active", icon: Clock3, color: "text-blue-500" },
  { key: "on_site_now", label: "On site now", icon: MapPin, color: "text-emerald-500" },
  { key: "pending_assignment", label: "Pending", icon: Clock3, color: "text-amber-500" },
  { key: "completed", label: "Completed", icon: CheckCircle2, color: "text-stone-400" },
  { key: "workers", label: "Workers", icon: Users, color: "text-violet-500" },
];
const REFRESH_MS = 30000;
const MAX_ROWS = 50;

// Active jobs: on site first, then in progress, not started, and future-dated last.
const activeRank = (j) => (j.scheduled_date && j.scheduled_date > todayLocal() ? 3 : j.open_entry_id ? 0 : (j.total_seconds || 0) > 0 ? 1 : 2);
const byActive = (a, b) => activeRank(a) - activeRank(b) || (a.scheduled_date || "").localeCompare(b.scheduled_date || "");
const byDone = (a, b) => (b.completed_at || "").localeCompare(a.completed_at || "");

const PANELS = {
  total_jobs: { title: "All jobs", empty: "No jobs yet." },
  active: { title: "Active jobs", empty: "No active jobs right now." },
  on_site_now: { title: "Who's on site now", empty: "Nobody is checked in at a job right now." },
  pending_assignment: { title: "Waiting for crew to sign in", empty: "Every job's crew member has signed in." },
  completed: { title: "Completed jobs", empty: "No completed jobs yet." },
  workers: { title: "Crew", empty: "No crew yet. Add them in the Crew tab." },
};

const rowsFor = (key, d, onOpen) => {
  const active = d.jobs.filter((j) => j.status !== "completed").sort(byActive);
  const done = d.jobs.filter((j) => j.status === "completed").sort(byDone);
  if (key === "on_site_now") return d.onSite.map((p) => <OnSiteRow key={`${p.user_id}-${p.job_id}`} p={p} now={d.now} onOpen={onOpen} />);
  if (key === "workers") {
    return d.workers.map((w) => <WorkerRow key={w.email} w={w} here={d.onSite.find((p) => p.email === w.email)} onOpen={onOpen} />);
  }
  if (key === "pending_assignment") return d.jobs.filter((j) => !j.assigned_to).map((j) => <PendingRow key={j.id} job={j} onOpen={onOpen} />);
  const list = key === "active" ? active : key === "completed" ? done : [...active, ...done];
  return list.map((j) => <JobRow key={j.id} job={j} onOpen={onOpen} />);
};

const OverviewPanel = ({ k, data, onOpen, onClose }) => {
  const ref = useRef(null);
  useEffect(() => { ref.current?.scrollIntoView({ behavior: "smooth", block: window.innerWidth < 1024 ? "start" : "nearest" }); }, [k]);
  const rows = data ? rowsFor(k, data, onOpen) : null;
  return (
    <motion.section ref={ref} key={k} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      className="mt-4 bg-card border border-border rounded-2xl overflow-hidden scroll-mt-24" data-testid={`overview-panel-${k}`}>
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-border bg-secondary/40">
        <h3 className="font-display font-semibold">{PANELS[k].title}{rows ? <span className="text-muted-foreground font-normal"> ({rows.length})</span> : null}</h3>
        <button type="button" onClick={onClose} aria-label="Close list" data-testid="overview-panel-close" className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-secondary transition-colors"><X className="w-4 h-4" /></button>
      </div>
      {!rows ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        : rows.length === 0 ? <p className="px-5 py-8 text-sm text-muted-foreground text-center" data-testid="overview-panel-empty">{PANELS[k].empty}</p>
          : <div className="max-h-[60vh] overflow-y-auto">{rows.slice(0, MAX_ROWS)}</div>}
      {rows && rows.length > MAX_ROWS && <p className="px-5 py-3 text-xs text-muted-foreground border-t border-border">Showing the first {MAX_ROWS} of {rows.length}. The Jobs tab has the full list and search.</p>}
    </motion.section>
  );
};

export const OverviewTab = ({ onNewJob, onOpenJob }) => {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [workers, setWorkers] = useState(null);
  const [open, setOpen] = useState(null);
  const [updated, setUpdated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const { logout } = useAuth();
  const { company } = useCompany();
  const openRef = useRef(open);
  openRef.current = open;

  const load = useCallback(async () => {
    const wantCrew = openRef.current === "workers";
    const [s, j, w] = await Promise.all([api.stats(), api.jobs().catch(() => null), wantCrew ? api.workers().catch(() => null) : null]);
    setStats(s);
    if (j) setJobs(j);
    if (w) setWorkers(w);
    setUpdated(new Date());
  }, []);

  useEffect(() => {
    load().catch(() => {});
    const id = setInterval(() => { if (!document.hidden) load().catch(() => {}); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => { if (open === "workers" && !workers) api.workers().then(setWorkers).catch(() => setWorkers([])); }, [open, workers]);

  const refresh = async () => {
    setBusy(true);
    try { await load(); } catch { toast.error("Could not refresh"); } finally { setBusy(false); }
  };

  const isEmpty = company?.legacy_sync && stats && (stats.total_jobs ?? 0) === 0 && (stats.workers ?? 0) === 0;

  const restore = async () => {
    setRestoring(true);
    try {
      const r = await api.restoreData();
      const c = r.counts || {};
      toast.success(`Restored ${c.jobs || 0} jobs and ${c.crew || 0} crew — please sign in again.`);
      setTimeout(() => { logout(); }, 2500);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) toast.info(e?.response?.data?.detail || "This app already has data — nothing to restore.");
      else toast.error(e?.response?.data?.detail || "Restore failed — please try again.");
      setRestoring(false);
    }
  };

  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="fade-up">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold mb-1">Overview</h2>
          <p className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap" data-testid="overview-updated">
            Live snapshot of your crew and jobs. Tap a card to see the list.
            {updated && <span className="inline-flex items-center gap-1 text-xs">· Updated {updated.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: timeZone() })}
              <button type="button" onClick={refresh} disabled={busy} aria-label="Refresh" data-testid="overview-refresh-btn" className="w-6 h-6 inline-flex items-center justify-center rounded-full hover:bg-secondary transition-colors">
                <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
              </button></span>}
          </p>
        </div>
        {onNewJob && (
          <Button data-testid="overview-new-job-btn" onClick={onNewJob} className="rounded-full bg-primary hover:bg-primary/90 text-white shrink-0">
            <Plus className="w-4 h-4 mr-2" /> New Job
          </Button>
        )}
      </div>

      {isEmpty && (
        <div className="mb-6 rounded-2xl border border-primary/30 bg-primary/5 p-5" data-testid="restore-data-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <DownloadCloud className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">Bring your data across</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                This app looks empty. Tap below to load your jobs, crew, timesheets, clients and roster from your old app. You&apos;ll be asked to sign in again once it&apos;s done.
              </p>
              <Button data-testid="restore-data-btn" onClick={restore} disabled={restoring} className="mt-3 rounded-full bg-primary hover:bg-primary/90 text-white">
                {restoring ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restoring…</> : <><DownloadCloud className="w-4 h-4 mr-2" /> Restore my data</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((c) => {
          const Icon = c.icon;
          const on = open === c.key;
          return (
            <button key={c.key} type="button" data-testid={`stat-${c.key}`} aria-expanded={on} onClick={() => setOpen(on ? null : c.key)}
              className={`group text-left bg-card border rounded-2xl p-5 sm:p-6 hover:shadow-lg hover:border-primary/40 transition-[box-shadow,border-color] ${on ? "border-primary ring-2 ring-primary/20" : "border-border"}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{c.label}</span>
                <Icon className={`w-5 h-5 ${c.color}`} />
              </div>
              <p className="font-display text-4xl font-black mt-3">{stats[c.key] ?? 0}</p>
              <p className="mt-2 text-xs text-muted-foreground group-hover:text-primary inline-flex items-center gap-1 transition-colors">
                {on ? "Hide list" : "View list"} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${on ? "rotate-180" : ""}`} />
              </p>
            </button>
          );
        })}
      </div>
      {open && (
        <OverviewPanel k={open} onOpen={onOpenJob} onClose={() => setOpen(null)}
          data={jobs && (open !== "workers" || workers) ? { jobs, workers: workers || [], onSite: stats.on_site || [], now: updated?.getTime() || Date.now() } : null} />
      )}
    </div>
  );
};
